import { useTranslation } from 'react-i18next'
import { AlertTriangle, Check, X, FileText, Smartphone, Search, ShoppingBag } from 'lucide-react'
import type { AuditReport } from '@/lib/api/amazon-listing'
import { SectionCard } from './shared/SectionCard'
import { RecommendationCards } from './shared/RecommendationCards'
import { SalesPsychologyCard } from './shared/SalesPsychologyCard'

interface DescriptionAnalysisProps {
  analysis: AuditReport['description_analysis']
}

export function DescriptionAnalysis({ analysis }: DescriptionAnalysisProps) {
  const { t } = useTranslation()
  if (!analysis) return null
  const r = (key: string) => t(`amazonListing.report.${key}`)
  const html = analysis.html_structure || {}
  const aplus = analysis.aplus_recommendation || {}
  const seo = analysis.seo_analysis || {}
  const cross = analysis.cross_selling || {}

  return (
    <div className="space-y-4">
      <SectionCard title={r('descriptionAnalysis')} score={analysis.score}>
        {analysis.analysis_incomplete && (
          <div className="flex items-center gap-2 p-3 mb-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {r('analysisIncomplete')}
          </div>
        )}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-4">
          <StructureBadge label={r('formatting')} ok={html.has_formatting} />
          <StructureBadge label={r('headers')} ok={html.has_headers} />
          <StructureBadge label={r('lists')} ok={html.has_lists} />
        </div>
        {analysis.mobile_readability && (
          <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
            <Smartphone className="w-4 h-4" />
            <span>{r('mobileReadability')}: {analysis.mobile_readability.score}/100</span>
          </div>
        )}
      </SectionCard>

      {(seo.keywords_found?.length > 0 || seo.keywords_missing?.length > 0) && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
          <h4 className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
            <Search className="w-4 h-4 text-neutral-500" /> {r('seoAnalysis')}
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {seo.keywords_found?.length > 0 && <KeywordList items={seo.keywords_found} color="green" label={r('keywordsFound')} />}
            {seo.keywords_missing?.length > 0 && <KeywordList items={seo.keywords_missing} color="red" label={r('keywordsMissing')} />}
          </div>
        </div>
      )}

      {aplus.should_use && <AplusCard aplus={aplus} r={r} />}

      {cross.potential && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
          <h4 className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            <ShoppingBag className="w-4 h-4 text-neutral-500" /> {r('crossSelling')}
          </h4>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
            {r('potential')}: {cross.potential}
          </span>
          {cross.suggestions?.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm text-neutral-600 dark:text-neutral-400">
              {cross.suggestions.map((s, i) => (
                <li key={i}>• {typeof s === 'string' ? s : s.reason || s.product_type || JSON.stringify(s)}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {analysis.sales_psychology && <SalesPsychologyCard data={analysis.sales_psychology} />}
      {analysis.recommendations?.length > 0 && <RecommendationCards recs={analysis.recommendations} t={t} />}
    </div>
  )
}

function StructureBadge({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${ok ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400'}`}>
      {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {label}
    </span>
  )
}

function KeywordList({ items, color, label }: { items: string[]; color: 'green' | 'red'; label: string }) {
  const cls = color === 'green'
    ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'
  return (
    <div>
      <div className="text-xs text-neutral-500 mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1">
        {items.map((kw) => <span key={kw} className={`px-2 py-0.5 rounded-full text-xs ${cls}`}>{kw}</span>)}
      </div>
    </div>
  )
}

function AplusCard({ aplus, r }: { aplus: AuditReport['description_analysis']['aplus_recommendation']; r: (k: string) => string }) {
  return (
    <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-primary-200 dark:border-primary-800 p-4">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="w-4 h-4 text-primary-600" />
        <span className="text-sm font-medium text-primary-800 dark:text-primary-300">{r('aplusRecommended')}</span>
        {aplus.competitor_aplus_rate > 0 && (
          <span className="text-xs text-primary-600">({Math.round(aplus.competitor_aplus_rate * 100)}% {r('ofCompetitors')})</span>
        )}
      </div>
      <div className="space-y-1">
        {aplus.recommended_modules?.map((m, i) => (
          <p key={i} className="text-sm text-primary-700 dark:text-primary-400">
            <span className="font-medium">{m.module}:</span> {m.reason}
          </p>
        ))}
      </div>
    </div>
  )
}
